import moment from "moment";
import { toast } from "react-toastify";
import { getCookie } from "../utils/cookiesHelper";
import CHAT_ACTIONS_TYPES from "./actionTypes/chatActionTypes";

export const CHAT_INITIAL_STATE = {
  orgUserToken: getCookie("X-User-Token"),
  jwtToken: getCookie("chatJwtToken") || null,
  userAgentData: {},
  isTyping: false,
  chatWidgetSettings: {},
  rooms: [],
  roomChat: [],
  chatResponse: {}, // Store complete chat response data
  customerId: null,
  selectedRoom: {},
  joinedRoomBefore: false,
  loadingRooms: true,
  loadingChat: true,
  searchQueryForRoom: null,
  // searchKey is used for handle CTRL+F for search
  searchKey: "rooms",
  searchQueryForMessage: null,
  searchMessagesResult: [],
  images: [],
  imagesUploadProgress: {},
  attachments: {},
  sendPromoModal: false,
  promoCodeSelected: null,
  sendProductsModal: false,
  filterQueryForRooms: null,

  // Group chat
  activeChatTab: "direct",
  groupRooms: [],
  loadingGroupRooms: false,
  selectedGroupRoom: null,
  groupMessages: [],
  loadingGroupChat: false,
  groupPage: 0,
  groupHasMore: true,
  isGroupJoined: false,
};

const chatReducer = (state = CHAT_INITIAL_STATE, action) => {
  switch (action.type) {
    case CHAT_ACTIONS_TYPES.SET_JWT_TOKEN: {
      return {
        ...state,
        jwtToken: action?.payload?.jwtToken,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_AGENT_USER_DATA: {
      return {
        ...state,
        userAgentData: action?.payload?.userAgentData,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_ROOMS: {
      const roomsSorted = action?.payload?.rooms?.sort((a, b) => {
        // Sort by latestMessage.createdAt if available, otherwise fallback to updatedAt
        const aTime = a.latestMessage?.createdAt || a.updatedAt;
        const bTime = b.latestMessage?.createdAt || b.updatedAt;

        // Ensure timezone-aware comparison
        const aMoment = aTime ? moment(aTime.endsWith("Z") ? aTime : aTime + "Z") : moment(0);
        const bMoment = bTime ? moment(bTime.endsWith("Z") ? bTime : bTime + "Z") : moment(0);
        return aMoment.isAfter(bMoment) ? -1 : 1;
      });
      return {
        ...state,
        rooms: roomsSorted,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_ROOM_CHAT: {
      return {
        ...state,
        roomChat: action?.payload?.roomChat,
        chatResponse: action?.payload?.chatResponse || {},
      };
    }

    case CHAT_ACTIONS_TYPES.SET_CHAT_WIDGET_SETTINGS: {
      return {
        ...state,
        chatWidgetSettings: action?.payload?.chatWidgetSettings,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_TYPING: {
      if ((action?.payload?.roomId === (state?.selectedRoom?._id || state?.selectedRoom?.chatId)) || action?.payload?.force) {
        return {
          ...state,
          isTyping: action?.payload?.isTyping,
        };
      }
      return {
        ...state,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_SELECTED_ROOM: {
      return {
        ...state,
        selectedRoom: action?.payload?.selectedRoom,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_CUSTOMER_ID: {
      return {
        ...state,
        customerId: action?.payload?.customerId,
      };
    }

    case CHAT_ACTIONS_TYPES.ADD_NEW_MESSAGE_TO_ROOM: {
      const newMessage = action?.payload?.newMessage;
      const currentChatId = state?.selectedRoom?._id || state?.selectedRoom?.chatId || action?.payload?.currentChatId;

      const messageBelongsToCurrentChat =
        newMessage?.chat?._id === currentChatId ||
        newMessage?.chatId === currentChatId ||
        (typeof newMessage?._id === "string" && newMessage?._id?.startsWith("temp_"));

      let updatedRoomChat = state.roomChat;
      if (messageBelongsToCurrentChat || action?.payload?.force === true) {
        const nowTs = Date.now();
        const isServerConfirmed = !String(newMessage?._id).startsWith("temp_") && (newMessage?.messageId || newMessage?.raw);

        if (isServerConfirmed) {
          updatedRoomChat = state.roomChat.filter((msg) => {
            const isTemp = String(msg?._id).startsWith("temp_");
            if (!isTemp) return true;
            const sameContent = msg?.content === newMessage?.content;
            const sameSender = (msg?.sender?.isAgent === true) || (msg?.sender?._id && msg?.sender?._id === newMessage?.sender?.id);
            const withinWindow = (() => {
              const msgTs = msg?.createdAt ? new Date(msg.createdAt).getTime() : nowTs;
              return Math.abs(nowTs - msgTs) <= 15000;
            })();
            return !(sameContent && sameSender && withinWindow);
          });
        }

        updatedRoomChat = [...updatedRoomChat, newMessage];
      }

      // Update rooms array latestMessage
      const updatedRooms = state.rooms.map((room) => {
        const roomId = room._id?.toString();
        const chatId = room.chatId?.toString();
        const newChatId = newMessage.chatId?.toString() || newMessage.chat?._id?.toString();

        if (roomId === newChatId || chatId === newChatId) {
          return {
            ...room,
            latestMessage: newMessage,
            updatedAt: newMessage.createdAt || new Date().toISOString(),
          };
        }
        return room;
      });

      // Update selectedRoom.latestMessage if it matches
      const updatedSelectedRoom =
        state.selectedRoom &&
          (state.selectedRoom._id === newMessage.chatId || state.selectedRoom.chatId === newMessage.chatId)
          ? { ...state.selectedRoom, latestMessage: newMessage }
          : state.selectedRoom;

      return {
        ...state,
        roomChat: updatedRoomChat,
        rooms: updatedRooms,
        selectedRoom: updatedSelectedRoom,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_LOADING_ROOMS: {
      return {
        ...state,
        loadingRooms: action?.payload?.loadingRooms,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_LOADING_CHAT: {
      return {
        ...state,
        loadingChat: action?.payload?.loadingChat,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_MESSAGE_SEEN: {
      return {
        ...state,
        roomChat: state.roomChat.map((message) =>
          (message._id === action?.payload?.messageId
            ? { ...message, seen: true }
            : message)
        ),
      };
    }

    case CHAT_ACTIONS_TYPES.SET_JOINED_ROOM_BEFORE: {
      return {
        ...state,
        joinedRoomBefore: action?.payload?.joinedRoomBefore,
      };
    }

    case CHAT_ACTIONS_TYPES.SEARCH_FOR_ROOM: {
      return {
        ...state,
        searchQueryForRoom: action?.payload?.searchQueryForRoom,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_SEARCH_KEY: {
      return {
        ...state,
        searchKey: action?.payload?.searchKey,
      };
    }

    case CHAT_ACTIONS_TYPES.SEARCH_FOR_MESSAGE: {
      return {
        ...state,
        searchQueryForMessage: action?.payload?.searchQueryForMessage,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_SEARCH_MESSAGES_RESULT: {
      return {
        ...state,
        searchMessagesResult: action?.payload?.searchMessagesResult,
      };
    }

    case CHAT_ACTIONS_TYPES.APPEND_NEW_ROOM: {
      return {
        ...state,
        rooms: [action?.payload?.newRoom, ...state.rooms]
      };
    }

    case CHAT_ACTIONS_TYPES.REMOVE_JWT_TOKEN: {
      return {
        ...state,
        jwtToken: null,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_IMAGES_FOR_UPLOAD: {
      return {
        ...state,
        images: [...state.images, ...action.payload.images],
      };
    }

    case CHAT_ACTIONS_TYPES.SET_IMAGES_UPLOAD_PROGRESS: {
      return {
        ...state,
        imagesUploadProgress: {
          ...state.imagesUploadProgress,
          ...action.payload.imagesUploadProgress,
        },
      };
    }

    case CHAT_ACTIONS_TYPES.SET_ATTACHMENTS: {
      return {
        ...state,
        attachments: { ...state.attachments, ...action.payload.attachments },
      };
    }

    case CHAT_ACTIONS_TYPES.REMOVE_IMAGE_ATTACHMENT: {
      const fileNameToRemove = action.payload.fileName;

      const imagesAfterRemove = state.images.filter(
        (file) => file.name !== fileNameToRemove
      );

      const imagesUploadProgressAfterRemove = { ...state.imagesUploadProgress };
      delete imagesUploadProgressAfterRemove[fileNameToRemove];

      const attachmentsAfterRemove = { ...state.attachments };
      delete attachmentsAfterRemove[fileNameToRemove];

      return {
        ...state,
        images: imagesAfterRemove,
        imagesUploadProgress: imagesUploadProgressAfterRemove,
        attachments: attachmentsAfterRemove,
      };
    }

    case CHAT_ACTIONS_TYPES.REMOVE_ALL_ATTACHMENTS: {
      return {
        ...state,
        attachments: [],
        imagesUploadProgress: {},
        filesUploadProgress: {},
        images: [],
        files: [],
      };
    }

    case CHAT_ACTIONS_TYPES.SET_PROMO_CODE_MODAL: {
      return {
        ...state,
        sendPromoModal: action?.payload?.sendPromoModal
      };
    }

    case CHAT_ACTIONS_TYPES.SET_PROMO_CODE: {
      return {
        ...state,
        promoCodeSelected: action?.payload?.promoCodeSelected
      };
    }

    case CHAT_ACTIONS_TYPES.SET_PRODUCTS_MODAL: {
      return {
        ...state,
        sendProductsModal: action?.payload?.sendProductsModal
      };
    }

    case CHAT_ACTIONS_TYPES.FILTER_QUERY_FOR_ROOMS: {
      return {
        ...state,
        filterQueryForRooms: action?.payload?.filterQueryForRooms,
      };
    }

    case CHAT_ACTIONS_TYPES.UPDATE_ROOM_CONTENT: {
      const newRooms = state.rooms.map((room) => (room._id === action.payload.roomUpdated._id ? action.payload.roomUpdated : room));
      return {
        ...state,
        rooms: newRooms,
      };
    }

    case CHAT_ACTIONS_TYPES.UPDATE_LATEST_MESSAGE_AND_SORT: {
      const roomIsReplaced = state.rooms.map((room) => {
        if (room._id === action.payload.roomUpdated._id || room.chatId === action.payload.roomUpdated._id) {
          return {
            ...room,
            latestMessage: action.payload.roomUpdated.latestMessage,
            updatedAt: action.payload.roomUpdated.updatedAt
          };
        }
        return room;
      });
      const roomsSorted = roomIsReplaced.sort((a, b) => {
        // Sort by latestMessage.createdAt if available, otherwise fallback to updatedAt
        const aTime = a.latestMessage?.createdAt || a.updatedAt;
        const bTime = b.latestMessage?.createdAt || b.updatedAt;

        // Ensure timezone-aware comparison
        const aMoment = aTime ? moment(aTime.endsWith("Z") ? aTime : aTime + "Z") : moment(0);
        const bMoment = bTime ? moment(bTime.endsWith("Z") ? bTime : bTime + "Z") : moment(0);
        return aMoment.isAfter(bMoment) ? -1 : 1;
      });
      return {
        ...state,
        rooms: roomsSorted,
      };
    }

    case CHAT_ACTIONS_TYPES.MARK_LATEST_MESSAGE_AS_SEEN: {
      const updatedRooms = state.rooms.map((room) => {
        if (room._id === action.payload.roomId || room.chatId === action.payload.roomId) {
          return {
            ...room,
            latestMessage: {
              ...room.latestMessage,
              seen: true
            }
          };
        }
        return room;
      });
      return {
        ...state,
        rooms: updatedRooms,
      };
    }

    case CHAT_ACTIONS_TYPES.FIRE_ERROR_MESSAGE: {
      if (action?.payload?.roomId === (state?.selectedRoom?._id || state?.selectedRoom?.chatId)) {
        toast.error(action.payload.message);
        return {
          ...state,
        };
      }
      return {
        ...state,
      };
    }

    case CHAT_ACTIONS_TYPES.FIRE_NOTIFY_MESSAGE: {
      if (action?.payload?.roomId === (state?.selectedRoom?._id || state?.selectedRoom?.chatId)) {
        toast(action.payload.message);
        return {
          ...state,
        };
      }
      return {
        ...state,
      };
    }

    // ---- Group chat cases ----

    case CHAT_ACTIONS_TYPES.SET_ACTIVE_CHAT_TAB: {
      return { ...state, activeChatTab: action.payload.activeChatTab };
    }

    case CHAT_ACTIONS_TYPES.SET_GROUP_ROOMS: {
      return { ...state, groupRooms: action.payload.groupRooms };
    }

    case CHAT_ACTIONS_TYPES.SET_LOADING_GROUP_ROOMS: {
      return { ...state, loadingGroupRooms: action.payload.loadingGroupRooms };
    }

    case CHAT_ACTIONS_TYPES.SET_SELECTED_GROUP_ROOM: {
      return {
        ...state,
        selectedGroupRoom: action.payload.selectedGroupRoom,
        groupMessages: [],
        groupPage: 0,
        groupHasMore: true,
        isGroupJoined: false,
      };
    }

    case CHAT_ACTIONS_TYPES.SET_GROUP_MESSAGES: {
      // Reverse because API returns newest-first; we display oldest-first
      const reversed = [...(action.payload.groupMessages || [])].reverse();
      return { ...state, groupMessages: reversed };
    }

    case CHAT_ACTIONS_TYPES.APPEND_OLDER_GROUP_MESSAGES: {
      const reversed = [...(action.payload.groupMessages || [])].reverse();
      return { ...state, groupMessages: [...reversed, ...state.groupMessages] };
    }

    case CHAT_ACTIONS_TYPES.ADD_NEW_GROUP_MESSAGE: {
      const incoming = action.payload.newMessage;
      // Deduplicate: skip if same messageId already present
      const existsById = state.groupMessages.some(
        (m) => m.messageId && incoming.messageId && m.messageId === incoming.messageId
      );
      if (existsById) return state;

      // Replace optimistic temp message with same content
      const isServerConfirmed = incoming.messageId && !String(incoming._id || "").startsWith("temp_");
      if (isServerConfirmed) {
        const withoutTemp = state.groupMessages.filter((m) => {
          if (!String(m._id || "").startsWith("temp_")) return true;
          return m.content !== incoming.content;
        });
        return { ...state, groupMessages: [...withoutTemp, incoming] };
      }
      return { ...state, groupMessages: [...state.groupMessages, incoming] };
    }

    case CHAT_ACTIONS_TYPES.SET_GROUP_JOINED: {
      return { ...state, isGroupJoined: action.payload.isGroupJoined };
    }

    case CHAT_ACTIONS_TYPES.SET_LOADING_GROUP_CHAT: {
      return { ...state, loadingGroupChat: action.payload.loadingGroupChat };
    }

    case CHAT_ACTIONS_TYPES.SET_GROUP_HAS_MORE: {
      return { ...state, groupHasMore: action.payload.groupHasMore };
    }

    case CHAT_ACTIONS_TYPES.SET_GROUP_PAGE: {
      return { ...state, groupPage: action.payload.groupPage };
    }

    default:
      return state;
  }
};

export default chatReducer;
